import styled, { keyframes, css } from 'styled-components';
import { PAYMENT_CONTENT_WIDTH, RECEIPT_PAGE_STATIC_ELEMENTS_HEIGHT } from 'shared/consts';
import { ModalWrapper } from '@hqo/react-components-library/dist/molecules/modals/wrapper';
import { TwoColumnModal } from '@hqo/react-components-library/dist/molecules/modals/twoColumnModal';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

export const ReceiptContainer = styled.div<{ isFullscreen?: boolean }>`
  & *::-webkit-scrollbar {
    width: 8px !important;
    background: ${({ theme }) => theme.colors.$transparent} !important;
  }

  & *::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.palette.sharedMediumGrey} !important;
    border-radius: 4px !important;
  }
`;

export const StyledTwoColumnModal = styled(TwoColumnModal)<{ isFullscreen?: boolean }>`
  & *::-webkit-scrollbar {
    width: 8px !important;
    background: ${({ theme }) => theme.colors.$transparent} !important;
  }

  & *::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.palette.sharedMediumGrey} !important;
    border-radius: 4px !important;
  }

  /*
   * THE LEFT PANE MUST SCROLL, AND MAKING IT SCROLL TAKES THE WHOLE CHAIN - RES-1326.
   *
   * Reported from production: the receipt clips its own content at the TOP and the BOTTOM at once,
   * which is the tell. It is not a scroll that has run out, it is a CENTRED OVERFLOW. This component
   * IS the library's ModalContainer (TwoColumnModal passes our className straight onto it), and the
   * library declares height:538px with align-items:center. A column taller than 538 is therefore
   * centred and spills equally both ways, and no ancestor scrolls.
   *
   * So overflow-y on the pane alone does nothing: nothing above it has a definite height, and it
   * simply grows. Every link needs constraining, which is exactly what the isFullscreen block below
   * already does for its own case - this is the same recipe for the default case.
   *
   *   this            = library ModalContainer   height:538  align-items:center -> STRETCH
   *   & > div         = TwoColumnContent         flex column, flex:1            -> min-height:0
   *   & > div > div   = DoublePane               grid 1fr 1fr                   -> min-height:0
   *   .left-pane      = the grid item                                           -> scrolls
   *
   * min-height:0 on each link because a flex and a grid item both default to min-height:auto, which
   * refuses to shrink below content and would silently defeat the overflow.
   *
   * Desktop only. Below PAYMENT_CONTENT_WIDTH the grid collapses to one column and the sheet scrolls
   * as a whole, which was verified working on a device and must not change. The right pane already
   * carried overflow-y unconditionally; only the left was ever missing it.
   *
   * MEASURED, from the production screenshots, self-calibrated on CheckInButton's 48px:
   * content 404px in a 528px box before the add-on prompt, 673px after. The prompt is the straw,
   * but a building with OpenPath keys renders UnlockDorsLink and overflows this pane with no add-ons
   * at all - so this is a latent bug of its own, not merely a consequence of that card.
   */
  @media only screen and (min-width: ${PAYMENT_CONTENT_WIDTH}) {
    align-items: stretch;

    & > div {
      min-height: 0;

      & > div:first-child {
        min-height: 0;
      }
    }

    .left-pane {
      min-height: 0;
      overflow-y: auto;
      overflow-x: hidden;
    }
  }

  ${({ isFullscreen }) =>
    isFullscreen &&
    css`
      width: 100%;
      height: 100%;
      max-width: 100%;
      max-height: 100%;
      animation: ${fadeIn} 0.3s ease-out;

      & > div {
        width: 100%;
        height: 100%;
        max-width: 100%;
        max-height: 100%;

        & > div:first-child {
          height: calc(100% - 50px);

          @media only screen and (max-width: ${PAYMENT_CONTENT_WIDTH}) {
            display: flex;
            flex-direction: column;
            min-height: 0;
          }
        }
      }

      .modal-wrapper-content {
        width: 100%;
        height: 100%;
        max-width: 100%;
        max-height: 100%;
        transform: none;
        animation: ${slideIn} 0.3s ease-out;

        & > div {
          width: 100%;
          height: 100%;
          max-width: 100vh;
          max-height: 100%;
        }
      }

      .left-pane {
        width: 100% !important;

        @media only screen and (max-width: ${PAYMENT_CONTENT_WIDTH}) {
          flex: 0 1 auto;
          min-height: 0;
          overflow-y: auto;
          padding: 0 16px;
        }
      }

      .right-pane {
        width: 100% !important;
        height: calc(100% - 10px);

        & > div:first-child {
          padding-bottom: 0;
        }

        @media only screen and (max-width: ${PAYMENT_CONTENT_WIDTH}) {
          flex: 1 1 auto;
          min-height: 0;
          height: auto !important;
          padding: 0 16px;
          overflow-y: auto;
          overflow-x: hidden;
        }
      }

      .button-container {
        width: 100% !important;
        justify-content: flex-end !important;
      }
    `}

  @media only screen and (max-width: ${PAYMENT_CONTENT_WIDTH}) {
    height: 100%;
    padding: 24px 0 0 0;

    .button-container {
      border-top: 1px solid ${({ theme }) => theme.colors.greys.adminGreyLight};
      padding: 12px 15px;
      width: 100%;
    }

    .right-pane {
      height: calc(100vh - ${RECEIPT_PAGE_STATIC_ELEMENTS_HEIGHT});
      padding: 0 16px;
      overflow-y: auto;
      overflow-x: hidden;
    }
  }
`;

export const LeftPaneContentContainer = styled.div<{ isFullscreen?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 24px;
  ${({ isFullscreen }) =>
    isFullscreen &&
    `
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
  `}
`;

export const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-self: flex-end;
  margin-top: 10px;
`;

export const DoublePane = styled.div<{ isFullscreen?: boolean; isActivityPane?: boolean }>`
  display: flex;
  flex-direction: ${({ isFullscreen }) => (isFullscreen ? 'row' : 'column')};
  /*
   * The non-fullscreen height reserves 150px for the phone chrome above the receipt. In the activity
   * pane (VIS-2032) there is no such chrome — the receipt owns the whole webview — so that reservation
   * renders as dead space below the content. Fill the pane there, like the fullscreen case.
   */
  height: ${({ isFullscreen, isActivityPane }) => (isFullscreen || isActivityPane ? '100vh' : 'calc(100vh - 150px)')};
  gap: 24px;
  flex-grow: 1;
  ${({ isFullscreen }) =>
    isFullscreen &&
    `
    width: 100%;
    overflow: hidden;
  `}
  /*
   * In the activity pane the receipt is a persistent, scrollable column that owns the whole webview
   * (the Done footer is dropped — see swipe-receipt-content). Fill the pane at 100vh and scroll within
   * when the content is taller, rather than spilling past the pane.
   */
  ${({ isActivityPane, isFullscreen }) =>
    isActivityPane &&
    !isFullscreen &&
    `
    overflow-y: auto;
    overflow-x: hidden;
  `}
`;

export const RightPane = styled.div<{ isFullscreen?: boolean }>`
  overflow-y: auto;
  overflow-x: hidden;
  ${({ isFullscreen }) =>
    isFullscreen &&
    `
    width: 50%;
    height: 100%;
    padding: 24px;
    box-sizing: border-box;
  `}
`;

export const LeftPane = styled.div<{ isFullscreen?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 24px;
  ${({ isFullscreen }) =>
    isFullscreen &&
    `
    width: 50%;
    height: 100%;
    padding: 24px;
    box-sizing: border-box;
    overflow-y: auto;
    overflow-x: hidden;
  `}
`;

export const StyledModalWrapper = styled(ModalWrapper)<{ isCancelBookingPage?: boolean }>`
  width: ${({ isCancelBookingPage }) => (isCancelBookingPage ? '560px' : PAYMENT_CONTENT_WIDTH)};

  /*
   * The content was flush to all four edges. Every sibling surface insets its own — the receipt's
   * TwoColumnModal and the add-on step's StepContent both do — so this one read as unfinished rather
   * than deliberate. The larger top value clears the absolutely-positioned exit button, which would
   * otherwise print over the first line of content; ConfirmedScreen adds 16px more inside.
   */
  .modal-wrapper-content {
    padding: 40px 16px 16px;
    box-sizing: border-box;
  }

  .modal-background {
    backdrop-filter: none;
    background-color: ${({ theme }) => `${theme.colorsRgba.black_5}`};
  }

  .modal-wrapper {
    ${({ isCancelBookingPage }) => isCancelBookingPage && 'height: 360px;'}
  }

  @media only screen and (max-width: ${PAYMENT_CONTENT_WIDTH}) {
    width: 100%;
    .modal-wrapper-exit-button {
      z-index: 15;
    }
  }
`;
